import type { AbstractAnalyticsDispatcher } from "@asurion-core/eventualize";
import type { CallIntroFlowQuestions } from "@expert/contentful/src/api/getCallIntroFlowQuestions";
import type { Partner } from "@expert/shared-types";
import type { CountryCode } from "libphonenumber-js";
import type { JSX } from "react";

import { AnalyticsProvider } from "@asurion-core/eventualize-react";
import { removeMdnFormatting } from "@expert/common-ui";
import { Box, Button, Container, Flex, Group, InputBase, Stack, Text, Tooltip } from "@mantine/core";
import { GetInputPropsReturnType } from "@mantine/form";
import { IconInfoCircle } from "@tabler/icons-react";
import { IMaskInput } from "react-imask";

import type { CustomerVerificationStep } from "../../../../stores/useVerificationFlowStore";
import type { CustomerDetailsPayload } from "../../../../types";
import type { ChannelShareData, PartnerProgram } from "./customer-info/types";
import type { NameMatchResult } from "./utils/compareName";

import { withPolymorphicClickAnalytics } from "../../../../shared-utils/analytics/withPolymorphicClickAnalytics";
import { CustomerHandoff } from "../customer-handoff/CustomerHandoff";
import { hasStandardIVRInfo } from "../customer-handoff/isIVRHandoffStep";
import { BillingAccountNumberAndPinSection } from "./BillingAccountNumberAndPinSection";
import { CallIntroQuestions } from "./CallIntroQuestions";
import { CustomerNamesSection } from "./CustomerNamesSection";
import { CustomerProgramSelection } from "./CustomerProgramSelection";
import classes from "./CustomerVerification.module.css";
import { LoadingAnimation } from "./LoadingAnimation";
import { SimplifiedMdnSection } from "./SimplifiedMdnSection";
import { MAX_MDN_REASON } from "./utils/calcIsConfirmationDisabled";

export interface CustomerVerificationViewProps {
    sessionId: string;
    currentStep: CustomerVerificationStep;

    config: {
        verificationType: "phoneNumber" | "billingAccountNumberAndPin";
        isInternationalPhoneEnabled: boolean;
        defaultCountry: CountryCode;
        orderIdMask: string | undefined;
        dispatcher: AbstractAnalyticsDispatcher;
        blocklistedNumbers: string[] | undefined;
        blocklistedPrefixes: string[] | undefined;
        isExpertUniversitySimulatedVoiceTask: boolean | undefined;
    };

    customer: {
        data: CustomerDetailsPayload | undefined;
        mdn: string;
        isLoading: boolean;
        nameFromIVR: string | undefined;
    };

    editing: {
        isEditingVerificationInput: boolean;
        isEditingCustomerInformation: boolean;
    };

    visibility: {
        showProgramSelection: boolean;
        showAdditionalDetailsEmailInput: boolean;
        showAdditionalDetailsOrderIdInput: boolean;
        showAdditionalDetailsAccountNumberInput: boolean;
        showAdditionalDetailsPhoneNumberInput: boolean;
    };

    programs: PartnerProgram[];

    ivr: {
        intent: string | undefined;
        summary: string | undefined;
        device: string | undefined;
        productLink: string | undefined;
        conversationalSummary: string | undefined;
        customerName: string | undefined;
        productTitle: string | undefined;
        brandPhoneNumber: string | undefined;
        supportPhoneNumber: string | undefined;
        orderNumber: string | undefined;
        warmTransferMessage: string | undefined;
        isWarmHandoffEnabled: string | undefined;
    };

    verificationForm: {
        getInputProps: (field: "billingAccountNumber" | "pin") => GetInputPropsReturnType;
        isValid: (field?: "billingAccountNumber" | "pin") => boolean;
        isDirty: (field?: "billingAccountNumber" | "pin") => boolean;
        getValues: () => { billingAccountNumber: string; pin: string };
        setFieldValue: (field: "billingAccountNumber" | "pin", value: string) => void;
        key: (field: "billingAccountNumber" | "pin") => string;
    };

    additionalDetailsForm: {
        getInputProps: (field: "email" | "orderId" | "accountNumber" | "mdn") => GetInputPropsReturnType;
        setFieldValue: (field: "email" | "orderId" | "accountNumber" | "mdn", value: string) => void;
        key: (field: "email" | "orderId" | "accountNumber" | "mdn") => string;
    };

    confirmation: {
        isDisabled: boolean;
        disabledReason: string | undefined;
        compareName: () => NameMatchResult;
    };

    handlers: {
        onConfirmClick: () => void;
        onMdnComplete: (mdn: string) => void;
        onMdnEditClick: () => void;
        onNameSelect: (name: string | undefined) => void;
        onBanSave: () => void;
        onBanEditClick: () => void;
        onSubscriptionsChange: (subscriptions: string[] | undefined) => void;
        onCustomerHandoffContinue: () => void;
    };

    callIntro: {
        partner: Partner | undefined;
        callDetails: ChannelShareData;
        questions: CallIntroFlowQuestions | undefined;
    };
}

export function CustomerVerificationView(props: CustomerVerificationViewProps): JSX.Element {
    const {
        sessionId,
        currentStep,
        config,
        customer,
        editing,
        visibility,
        ivr,
        verificationForm,
        additionalDetailsForm,
        confirmation,
        handlers,
        callIntro,
        programs,
    } = props;

    const ButtonWithAnalytics = withPolymorphicClickAnalytics(Button<"button">, "Click");

    // Generate unique key for CustomerNamesSection
    const customerNamesSectionKey = (() => {
        let keyBase = "";
        if (config.verificationType === "phoneNumber") {
            keyBase = customer.mdn;
        } else if (config.verificationType === "billingAccountNumberAndPin") {
            const { billingAccountNumber, pin } = verificationForm.getValues();
            keyBase = billingAccountNumber + pin;
        }
        return `names-section-${sessionId}-${keyBase}`;
    })();

    return (
        <AnalyticsProvider
            dispatcher={(dispatch) =>
                dispatch.withExtra({
                    newFlow: true,
                    page: currentStep,
                })
            }
        >
            {currentStep === "CustomerHandoff" && (
                <Flex id="customer-handoff-flexbox" className={classes.root}>
                    <CustomerHandoff
                        intent={ivr.intent}
                        conversationalSummary={ivr.conversationalSummary}
                        device={ivr.device}
                        productLink={ivr.productLink}
                        productTitle={ivr.productTitle}
                        customerName={ivr.customerName}
                    />
                    <Stack mt="xl" gap="xs" w="100%">
                        <ButtonWithAnalytics
                            size="sm"
                            fz="xs"
                            analytics={{ actionId: "CustomerHandoffContinue" }}
                            onClick={handlers.onCustomerHandoffContinue}
                            data-testid="customer-handoff-continue-button"
                        >
                            Continue
                        </ButtonWithAnalytics>
                    </Stack>
                </Flex>
            )}
            {currentStep === "CustomerPhoneInput" && (
                <Flex id="customer-confirmation-flexbox" className={classes.root}>
                    {ivr.isWarmHandoffEnabled?.toLowerCase() !== "true" &&
                        hasStandardIVRInfo({
                            intent: ivr.intent,
                            summary: ivr.summary,
                            device: ivr.device,
                            productLink: ivr.productLink,
                            productTitle: ivr.productTitle,
                            conversationalSummary: ivr.conversationalSummary,
                        }) &&
                        !editing.isEditingCustomerInformation && (
                            <CustomerHandoff
                                intent={ivr.intent}
                                summary={ivr.summary}
                                device={ivr.device}
                                productLink={ivr.productLink}
                                productTitle={ivr.productTitle}
                                customerName={ivr.customerName}
                            />
                        )}

                    {config.verificationType === "phoneNumber" && (
                        <SimplifiedMdnSection
                            key={`mdn-section-${sessionId}-${customer.mdn}`}
                            initialMdn={customer.mdn}
                            isEditing={editing.isEditingVerificationInput}
                            shouldAutoFocus={customer.mdn === "" && editing.isEditingVerificationInput}
                            onComplete={handlers.onMdnComplete}
                            onEditClick={handlers.onMdnEditClick}
                            isInternationalPhoneEnabled={config.isInternationalPhoneEnabled}
                            defaultCountry={config.defaultCountry}
                            dispatcher={config.dispatcher}
                            blocklistedNumbers={config.blocklistedNumbers}
                            blocklistedPrefixes={config.blocklistedPrefixes}
                            shouldSkipValidation={config.isExpertUniversitySimulatedVoiceTask}
                        />
                    )}

                    {config.verificationType === "billingAccountNumberAndPin" && (
                        <BillingAccountNumberAndPinSection
                            form={verificationForm}
                            isEditing={editing.isEditingVerificationInput}
                            onEditClick={handlers.onBanEditClick}
                            onSaveClick={handlers.onBanSave}
                        />
                    )}

                    {visibility.showAdditionalDetailsEmailInput && (
                        <Stack gap={0} w="100%">
                            <InputBase
                                data-testid="email-input"
                                classNames={{ input: `${classes.styledInput} fs-mask`, label: classes.styledLabel }}
                                style={{ display: customer.isLoading ? "none" : undefined }}
                                mt="xxl"
                                size="sm"
                                c="dark.1"
                                label="What's their email?"
                                key={`email-input-${additionalDetailsForm.key("email")}`}
                                {...additionalDetailsForm.getInputProps("email")}
                            />
                        </Stack>
                    )}

                    {customer.isLoading ? (
                        <Flex className={classes.animationRoot}>
                            <Container maw="160px">
                                <LoadingAnimation />
                            </Container>
                        </Flex>
                    ) : (
                        <>
                            <CustomerNamesSection
                                key={customerNamesSectionKey}
                                customer={customer.data ?? undefined}
                                customerNameFromIVR={customer.nameFromIVR}
                                onOptionSelect={handlers.onNameSelect}
                                isDisabled={
                                    config.verificationType === "phoneNumber"
                                        ? customer.mdn === ""
                                        : editing.isEditingVerificationInput
                                }
                            />

                            {visibility.showProgramSelection && <CustomerProgramSelection programs={programs} />}

                            <Stack gap={0} w="100%">
                                {visibility.showAdditionalDetailsPhoneNumberInput && (
                                    <InputBase
                                        data-testid="additional-details-phone-number-input"
                                        classNames={{
                                            input: `${classes.styledInput} fs-mask`,
                                            label: classes.styledLabel,
                                        }}
                                        mt="xxl"
                                        size="sm"
                                        c="dark.1"
                                        label="What's their phone number?"
                                        component={IMaskInput}
                                        mask="+1 (000) 000-0000"
                                        key={`phone-input-${additionalDetailsForm.key("mdn")}`}
                                        {...additionalDetailsForm.getInputProps("mdn")}
                                        onChange={undefined}
                                        onAccept={(value: string) =>
                                            additionalDetailsForm.setFieldValue("mdn", removeMdnFormatting(value))
                                        }
                                    />
                                )}

                                {visibility.showAdditionalDetailsOrderIdInput && (
                                    <InputBase
                                        data-testid="order-id-input"
                                        classNames={{
                                            input: `${classes.styledInput} fs-mask`,
                                            label: classes.styledLabel,
                                        }}
                                        mt="xxl"
                                        size="sm"
                                        c="dark.1"
                                        label="What's their order ID?"
                                        component={IMaskInput}
                                        mask={config.orderIdMask}
                                        key={`order-input-${additionalDetailsForm.key("orderId")}`}
                                        {...additionalDetailsForm.getInputProps("orderId")}
                                        onChange={undefined}
                                        onAccept={(value: string) =>
                                            additionalDetailsForm.setFieldValue("orderId", value)
                                        }
                                    />
                                )}

                                {visibility.showAdditionalDetailsAccountNumberInput && (
                                    <InputBase
                                        data-testid="additional-details-account-number-input"
                                        classNames={{
                                            input: `${classes.styledInput} fs-mask`,
                                            label: classes.styledLabel,
                                        }}
                                        mt="xxl"
                                        size="sm"
                                        c="dark.1"
                                        label={
                                            <Group gap={4} align="center">
                                                <Text size="sm" fw={500}>
                                                    Account Number
                                                </Text>
                                                <Tooltip
                                                    label="You can add the Osite or Opportunity Number in this field."
                                                    withArrow
                                                    multiline
                                                    w={220}
                                                >
                                                    <IconInfoCircle size={16} style={{ cursor: "pointer" }} />
                                                </Tooltip>
                                            </Group>
                                        }
                                        placeholder="Enter account number"
                                        key={`account-number-input-${additionalDetailsForm.key("accountNumber")}`}
                                        {...additionalDetailsForm.getInputProps("accountNumber")}
                                    />
                                )}
                            </Stack>
                        </>
                    )}

                    <Box mt={"sm"}>
                        <CallIntroQuestions
                            onSubscriptionsChange={handlers.onSubscriptionsChange}
                            partner={callIntro.partner}
                            callDetails={callIntro.callDetails}
                            questions={callIntro.questions}
                        />
                    </Box>

                    <Stack mt="24px" gap="xs">
                        {confirmation.disabledReason === MAX_MDN_REASON && (
                            <Text size="sm" c="error.5">
                                You&apos;ve reached the phone number edit limit, but you can still use a previous
                                number.
                            </Text>
                        )}

                        <ButtonWithAnalytics
                            size="sm"
                            fz="xs"
                            analytics={{
                                actionId: "Confirm",
                                extraContext: { customerNameSelected: confirmation.compareName() },
                            }}
                            onClick={handlers.onConfirmClick}
                            disabled={confirmation.isDisabled}
                            id="change-phone-number-button"
                            data-testid="change-phone-number-button"
                            title={confirmation.disabledReason}
                        >
                            Confirm
                        </ButtonWithAnalytics>
                    </Stack>
                </Flex>
            )}
        </AnalyticsProvider>
    );
}
